import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, Icon, IconTile, linkTextClasses, Metric } from '@evogard/ui';
import { count } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { MoneyValue, periodLabel } from './common.js';

/**
 * Os números do comercial no início da interna.
 *
 * ## Só venda. Nenhum número de remuneração entra aqui
 *
 * `GET /sales/numbers` fica sob `sales.sales:ver`, e por isso o cartão
 * carrega **apenas** o que essa permissão já concede. Comissão, meta e ranking
 * são outros três recursos: mostrá-los aqui faria uma permissão vazar as outras
 * — quem só pode ver vendas passaria a ver o custo de remuneração da base pelo
 * cartão da tela inicial, sem que a concessão tivesse dito isso em lugar nenhum.
 *
 * É por isso que este painel não tem um "R$ em comissões no mês", que seria o
 * número mais óbvio a pôr num painel de comercial.
 *
 * ## `sem_vendas` não é zero
 *
 * `sales_sales` nasce vazia nas duas praças. "Nenhuma venda nesta base, em
 * competência nenhuma" e "esta competência está zerada" produziriam a mesma tela
 * se o campo fosse um número — e exigem ações opostas: a primeira é importar, a
 * segunda é uma resposta sobre o mês.
 *
 * ## A fila de trabalho aparece, e é a razão de este painel valer a pena
 *
 * `withoutConsultant` são as vendas importadas com um código que ainda não casou com
 * nenhum consultor. Elas **não entram em apuração nenhuma**: comissão sem dono é
 * dinheiro que a apuração somou e ninguém pode conferir. Esconder o número
 * esconderia dinheiro que alguém vai deixar de receber, e o lugar de dizer isso
 * é a primeira tela que a operação abre de manhã.
 *
 * ## O cartão ao lado de "Minhas demandas"
 *
 * Desde 29/09/2026 o comercial é um `Card` na coluna do início, e não mais uma
 * fileira de `StatCard`: a quantidade de vendas desceu para a linha de apoio
 * do valor, e o destino é "Ver mais", na tela de vendas. A série dos meses
 * anteriores não está aqui porque `GET /sales/numbers` responde só a
 * competência corrente — um gráfico sem ela seria a tela inventando o passado.
 */
export function SalesPanel() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  /*
   * O escopo, e aqui ele não esconde nada: ele escolhe **o que as frases
   * afirmam**. `GET /sales/numbers` conta as mesmas linhas que a listagem, então
   * quem tem só `sales.sales:ver` recebe a própria carteira agregada — e as
   * duas frases deste arquivo falavam da base inteira.
   *
   * A pior das duas é `sem_vendas`: "nenhuma venda foi registrada nesta base, em
   * competência nenhuma" seria dito a um consultor novo numa praça com trezentas
   * vendas, e ele iria pedir a importação de um dado que já está lá.
   */
  const canSeeAny = can(context, 'sales.sales', 'ver_qualquer');

  const query = useQuery({
    queryKey: ['comercial', 'numeros', tenantId],
    queryFn: async () => {
      const r = await api.sales.numbers();
      return ok(r, 200, 'Não foi possível carregar os números.');
    },
    /*
     * Segura o resultado anterior só dentro da mesma base: a contagem de vendas
     * da CE sob o nome da GO seria uma afirmação sobre o desempenho de outra
     * praça — e é dela que sai a comissão de gente.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const n = query.data;

  return (
    <Card
      className="h-full"
      title={
        <span className="flex items-center gap-2">
          <Icon nome="grafico" className="text-texto-suave" />
          Visão comercial
        </span>
      }
      descricao={
        canSeeAny
          ? 'As vendas da competência nesta base.'
          : 'As suas vendas na competência — a sua carteira, não a da base.'
      }
      actions={
        <Link to="/sales/sales" className={linkTextClasses('gap-1 text-xs')}>
          Ver mais <Icon nome="seta-direita" />
        </Link>
      }
    >
      {query.isError ? (
        <p className="text-sm text-texto-suave">
          Não foi possível ler os números agora. A tela de vendas continua acessível.
        </p>
      ) : n?.estado === 'sem_vendas' ? (
        <WithoutSales broad={canSeeAny} />
      ) : (
        <>
          {/*
           * O número dentro do cartão, sem um segundo cartão em volta: a
           * pastilha e o `Metric` são a anatomia do `StatCard`, e a moldura é a
           * deste `Card` — cartão dentro de cartão desenha duas molduras para
           * a mesma coisa.
           */}
          <div className="flex items-center gap-3">
            <IconTile icon="carrinho" />
            <dl className="min-w-0 flex-1">
              <Metric
                label="Vendas no mês"
                value={
                  n?.estado === 'medido' ? <MoneyValue value={n.value} /> : undefined
                }
                support={
                  n?.estado === 'medido'
                    ? `${count(n.amount, 'venda', 'vendas')} na competência de ${periodLabel(n.period)}`
                    : undefined
                }
              />
            </dl>
          </div>

          {/*
           * Dito no cartão, e não só na tela de comissões: o valor da venda NÃO
           * é a comissão de ninguém. A comissão é R$ fixo por placa, escolhido
           * pela faixa do total do mês — quem lesse este número como base de um
           * percentual estaria lendo a regra do jeito que o repositório inteiro
           * existe para desfazer.
           */}
          {n?.estado === 'medido' && (
            <p className="mt-3 text-xs text-texto-suave">
              O valor é o que as vendas somam — não é a comissão de ninguém.
            </p>
          )}

          {n?.estado === 'medido' && (
            <QueueWithoutConsultant amount={n.withoutConsultant} />
          )}
        </>
      )}
    </Card>
  );
}

/**
 * Nunca houve venda — e o painel diz o que isso quer dizer, para QUEM está lendo.
 *
 * Nada de "nada aqui": a frase ensina qual é o estado e o que o destrava. Sem
 * ela, quem lê conclui que o mês está fraco.
 *
 * **As duas frases não são a mesma notícia com palavras diferentes.** Uma manda
 * importar a base; a outra manda conferir um vínculo de cadastro. Dizer a
 * primeira a quem só vê a própria carteira faria um consultor novo pedir a
 * importação de um dado que já está no banco — e a resposta de quem recebesse o
 * pedido seria "mas está tudo lá", com os dois olhando telas que concordam.
 */
function WithoutSales({ broad }: { broad: boolean }) {
  if (!broad) {
    return (
      <p className="text-sm">
        Nenhuma venda casada com você nesta base, em competência nenhuma. Isso não diz
        nada sobre a base — ela pode ter vendas de outras pessoas, e este cartão conta só
        a sua carteira. Se você vendeu e nada aparece, o que falta é o vínculo entre você
        e o seu código do ERP, e quem o resolve é quem cuida do cadastro de consultores.
      </p>
    );
  }

  return (
    <p className="text-sm">
      Nenhuma venda foi registrada nesta base, em competência nenhuma — então não há o que
      contar, e escrever “0 vendas no mês” aqui diria sobre o mês o que é ausência de
      cadastro. As vendas entram por importação do sistema atual ou uma a uma na tela de
      Vendas, no menu do comercial.
    </p>
  );
}

/**
 * A fila de vendas sem dono.
 *
 * Some quando é zero: uma linha permanente escrita "0" gastaria a atenção que
 * ela precisa comprar justamente no dia em que deixar de ser zero. É o mesmo
 * desenho de "fora da régua" no painel da cobrança.
 *
 * O tom é `alerta` no texto porque a consequência é dinheiro que ninguém
 * recebe — mas sem borda lateral colorida nem cartão dentro de cartão: a
 * separação é a linha de topo — `borda-suave`, porque ela separa DENTRO do
 * cartão.
 */
function QueueWithoutConsultant({ amount }: { amount: number }) {
  if (amount === 0) return null;

  return (
    <p className="mt-4 border-t border-borda-suave pt-3 text-sm">
      <strong className="font-medium text-alerta">
        {count(amount, 'venda', 'vendas')} sem consultor
      </strong>{' '}
      nesta competência. Elas não entram em apuração nenhuma enquanto não forem casadas
      com alguém — a comissão delas não é de ninguém. Na tela de Vendas, o filtro “sem
      consultor” mostra a fila.
    </p>
  );
}
